import React, { useEffect, useState } from 'react';
import SelectEntity from 'components/structure/Common/ListsEntity/SelectEntity';
import AccountService, { AccountProps } from 'services/hooks/account-service';
import { useAuth } from 'services/auth-service';
import SelectInput from 'components/elements/Form/select/selectInput';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { generateApiFailErrorMessage } from 'utils/helpers';
import OrganizationService, { OrganizationProps } from 'services/hooks/organization-service';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';

export default function RoleSelectionForm({ Controller, methods, editUUID }: any) {
  const {
    state: { user }
  } = useAuth();

  const [buildingOptions, setBuildingOptions] = useState<any>([]);
  const [orgOptions, setOrgOptions] = useState<any>([]);
  const [getAccountByIdResult, setGetAccountByIdResult] = useState<any>(undefined);

  const accountWatch = methods.watch('accountUuid');
  const buildingWatch = methods.watch('buildingUuid');
  const orgWatch = methods.watch('organizationUuid');

  const formatedOptions = (value: any, label: string) => {
    return { value: value, label: label };
  };

  // Fetch the account using getquery (GET request) by account uuid
  const { data } = useQuery<AccountProps, Error>(
    ['account', accountWatch],
    () => AccountService.findAccountById(accountWatch),
    {
      enabled: !!accountWatch && user?.role === 'SUPER',
      onError: () => {
        toast.error(generateApiFailErrorMessage('Account'));
      }
    }
  );

  useEffect(() => {
    if (data && accountWatch) {
      setGetAccountByIdResult(data);
    } else {
      setGetAccountByIdResult(undefined);
    }
  }, [accountWatch, data, methods]);

  useEffect(() => {
    if (!editUUID) {
      // Reset `buildingUuid` everytime accountWatch changes except when form is in Edit mode
      methods.setValue('buildingUuid', '');
    }
  }, [accountWatch, editUUID, methods]);

  // Fetch the organization using getquery (GET request) by organization uuid
  const { data: orgData } = useQuery<OrganizationProps, Error>(
    ['organization', orgWatch, !!editUUID],
    () => OrganizationService.findOrgById(orgWatch),
    {
      enabled: !!orgWatch && !!editUUID,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Organization'));
      }
    }
  );

  useEffect(() => {
    if (orgData && !!editUUID) {
      // On Edit mode, preload `buildingUuid` using the `organizationUuid`
      methods.setValue('buildingUuid', orgData?.buildingUuid);
    }
  }, [orgData, editUUID, methods]);

  // Query the `buildingWatch` using getquery (GET request) by building uuid
  const { data: buildingData } = useQuery<BuildingProps, Error>(
    ['building', buildingWatch, !!editUUID],
    () => BuildingService.findBuildingById(buildingWatch),
    {
      enabled: !!buildingWatch && !!editUUID,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Building'));
      }
    }
  );

  useEffect(() => {
    if (buildingData && !!editUUID) {
      // On Edit mode, preload `accountUuid` using the `buildingUuid`
      methods.setValue('accountUuid', buildingData?.accountUuid);
    }
  }, [buildingData, editUUID, methods]);

  // Populate Building and Organization options based on selected Account for `Super` role
  useEffect(() => {
    if (getAccountByIdResult && user?.role === 'SUPER') {
      // To populate Building options
      setBuildingOptions(
        getAccountByIdResult?.buildings?.map((building: { uuid: string; name: string }) =>
          formatedOptions(building?.uuid, building?.name)
        )
      );

      // To populate Organization options
      if (buildingWatch) {
        const populateSelectedOrg: { value: string; label: string }[] = [];
        getAccountByIdResult?.buildings?.map(
          (building: { uuid: string; organizations: any[] }) =>
            building?.uuid === buildingWatch &&
            building?.organizations?.map((org: { uuid: string; name: string }) =>
              populateSelectedOrg.push(formatedOptions(org?.uuid, org?.name))
            )
        );
        setOrgOptions(populateSelectedOrg);
      } else {
        const populateOrg: { value: string; label: string }[] = [];
        getAccountByIdResult?.buildings.forEach((building: { organizations: any[] }) =>
          building?.organizations?.map((org: { uuid: string; name: string }) =>
            populateOrg.push(formatedOptions(org?.uuid, org?.name))
          )
        );
        setOrgOptions(populateOrg);
      }

      if (!editUUID) {
        // Reset `organizationUuid` everytime buildingWatch changes except when form is in Edit mode
        methods.setValue('organizationUuid', '');
      }
    }
  }, [getAccountByIdResult, buildingWatch, methods, user?.role, editUUID]);

  const renderRoleBasedDetails = () => {
    return (
      <>
        <SelectEntity
          Controller={Controller}
          control={methods.control}
          label="Choose an Account"
          placeholder="Select Account"
          queryname="accounts"
          name="accountUuid"
          errors={methods.formState.errors}
          disabled={!!editUUID}
          query={AccountService.findAllAccounts}
        />
        <SelectInput
          Controller={Controller}
          control={methods.control}
          label="Choose a Building"
          placeholder="Select Building"
          name="buildingUuid"
          errors={methods.formState.errors}
          disabled={!!editUUID || !accountWatch}
          options={buildingOptions}
        />
        <SelectInput
          Controller={Controller}
          control={methods.control}
          label="Choose an Organization"
          placeholder="Select Organization"
          errors={methods.formState.errors}
          name="organizationUuid"
          disabled={!!editUUID || !buildingWatch}
          options={orgOptions}
        />
      </>
    );
  };

  return renderRoleBasedDetails();
}
